import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  User, Mail, Lock, CreditCard, Shield, Crown, ArrowLeft, Loader2,
  X, AlertTriangle, LogOut, Trash2, Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = { free: 'Free', pro: 'Pro', expert: 'Expert' };
const TIER_COLORS = { free: 'text-neutral-400', pro: 'text-primary', expert: 'text-amber-400' };

export default function AccountSettings() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = ['profile', 'security', 'subscription', 'emails'].includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'profile';

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-8" data-testid="account-settings-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Retour au dashboard
        </Link>

        <div className="flex items-start gap-4 mb-8">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${user?.is_founder ? 'bg-gradient-to-br from-amber-400/20 to-amber-600/10 border border-amber-400/40' : 'bg-primary/10 border border-primary/30'}`}>
            {user?.is_founder ? <Crown className="w-7 h-7 text-amber-400" /> : <User className="w-7 h-7 text-primary" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold tracking-tight">Paramètres du compte</h1>
              {user?.is_founder && (
                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-400 border border-amber-400/30">
                  Fondateur
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="grid grid-cols-4 mb-6 h-auto">
            <TabsTrigger value="profile" data-testid="tab-profile" className="text-xs sm:text-sm py-2">
              <User className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Profil</span>
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className="text-xs sm:text-sm py-2">
              <Shield className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Sécurité</span>
            </TabsTrigger>
            <TabsTrigger value="subscription" data-testid="tab-subscription" className="text-xs sm:text-sm py-2">
              <CreditCard className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Abonnement</span>
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails" className="text-xs sm:text-sm py-2">
              <Bell className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Emails</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <ProfileTab user={user} onLogout={() => { logout(); navigate('/'); }} />
          </TabsContent>
          <TabsContent value="security">
            <SecurityTab />
          </TabsContent>
          <TabsContent value="subscription">
            <SubscriptionTab user={user} refetchUser={refreshUser} />
          </TabsContent>
          <TabsContent value="emails">
            <EmailsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ==================== PROFILE ====================
function ProfileTab({ user, onLogout }) {
  const [showDelete, setShowDelete] = useState(false);
  return (
    <div className="space-y-4">
      <Section title="Informations personnelles" icon={User}>
        <Row label="Nom">{user?.name || '—'}</Row>
        <Row label="Email">{user?.email}</Row>
        <Row label="Membre depuis">
          {user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
        </Row>
        <Row label="Formule actuelle">
          <span className={`font-bold ${TIER_COLORS[user?.subscription_tier] || ''}`}>
            {TIER_LABELS[user?.subscription_tier] || user?.subscription_tier}
          </span>
          {user?.is_founder && <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-400">🏛 Fondateur</span>}
        </Row>
      </Section>

      <Section title="Actions" icon={LogOut}>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={onLogout} data-testid="logout-account-btn" className="flex-1">
            <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
          </Button>
          <Button variant="destructive" onClick={() => setShowDelete(true)} data-testid="open-delete-account" className="flex-1">
            <Trash2 className="w-4 h-4 mr-2" /> Supprimer mon compte
          </Button>
        </div>
      </Section>

      <DeleteAccountDialog open={showDelete} onClose={() => setShowDelete(false)} />
    </div>
  );
}

// ==================== SECURITY ====================
function SecurityTab() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error('Minimum 8 caractères');
    if (next !== confirm) return toast.error('Les mots de passe ne correspondent pas');
    if (next === current) return toast.error('Le nouveau mot de passe doit être différent');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mot de passe mis à jour');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Section title="Changer mon mot de passe" icon={Lock}>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-2">
            <Label>Mot de passe actuel</Label>
            <PasswordInput data-testid="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" />
          </div>
          <div className="space-y-2">
            <Label>Nouveau mot de passe</Label>
            <PasswordInput data-testid="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} autoComplete="new-password" />
            <p className="text-xs text-muted-foreground">Minimum 8 caractères.</p>
          </div>
          <div className="space-y-2">
            <Label>Confirmer le nouveau mot de passe</Label>
            <PasswordInput data-testid="confirm-new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} autoComplete="new-password" />
          </div>
          <Button type="submit" disabled={loading} data-testid="change-password-submit" className="w-full sm:w-auto">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {loading ? 'Enregistrement…' : 'Mettre à jour'}
          </Button>
        </form>
      </Section>
    </div>
  );
}

// ==================== SUBSCRIPTION ====================
function SubscriptionTab({ user, refetchUser }) {
  const [showCancel, setShowCancel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const tier = user?.subscription_tier || 'free';
  const isPaid = tier === 'pro' || tier === 'expert';

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/api/stripe/billing-history`, { headers: { Authorization: `Bearer ${token}` } });
        setHistory(data.transactions || []);
      } catch { /* ignore */ }
    };
    load();
  }, []);

  const cancel = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/stripe/cancel-subscription`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Votre abonnement sera résilié à la fin de la période en cours.');
      setShowCancel(false);
      if (refetchUser) await refetchUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la résiliation');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Section title="Ma formule" icon={CreditCard}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className={`text-2xl font-bold ${TIER_COLORS[tier]}`}>{TIER_LABELS[tier] || tier}</div>
            {user?.is_founder && <div className="text-[10px] uppercase tracking-widest text-amber-400 mt-1">🏛 Programme Fondateur — accès garanti</div>}
            {user?.subscription_status === 'canceling' && (
              <div className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Résiliation programmée en fin de période
              </div>
            )}
          </div>
          <Link to="/subscribe">
            <Button data-testid="change-plan-btn">
              {tier === 'free' ? 'Choisir un abonnement' : 'Changer de formule'}
            </Button>
          </Link>
        </div>
      </Section>

      {isPaid && !user?.is_founder && user?.subscription_status !== 'canceling' && (
        <Section title="Résilier mon abonnement" icon={AlertTriangle}>
          <p className="text-sm text-muted-foreground mb-3">
            La résiliation prend effet à la fin de votre période de facturation. Vous conservez l&apos;accès jusque là.
          </p>
          <Button variant="outline" onClick={() => setShowCancel(true)} data-testid="open-cancel-sub" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
            <X className="w-4 h-4 mr-2" /> Résilier
          </Button>
        </Section>
      )}

      {history.length > 0 && (
        <Section title="Historique de facturation" icon={CreditCard}>
          <div className="divide-y divide-border">
            {history.slice(0, 10).map((tx) => (
              <div key={tx.id} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{tx.tier || 'Paiement'}</div>
                  <div className="text-xs text-muted-foreground">
                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString('fr-FR') : '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono">{typeof tx.amount === 'number' ? `${tx.amount.toFixed(2)} €` : '—'}</div>
                  <div className={`text-[10px] uppercase ${tx.status === 'paid' ? 'text-primary' : 'text-muted-foreground'}`}>{tx.status || '—'}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent data-testid="cancel-sub-dialog">
          <DialogHeader>
            <DialogTitle>Résilier votre abonnement ?</DialogTitle>
            <DialogDescription>
              Vous conserverez votre accès {TIER_LABELS[tier]} jusqu&apos;à la fin de la période en cours. Aucun prélèvement supplémentaire ne sera effectué.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>Annuler</Button>
            <Button variant="destructive" onClick={cancel} disabled={loading} data-testid="confirm-cancel-sub">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirmer la résiliation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== EMAILS ====================
function EmailsTab() {
  const [prefs, setPrefs] = useState({ marketing: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/api/account/email-preferences`, { headers: { Authorization: `Bearer ${token}` } });
        setPrefs(data);
      } catch { /* ignore */ } finally { setLoading(false); }
    };
    load();
  }, []);

  const toggle = async (value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/account/email-preferences`, { marketing: value }, { headers: { Authorization: `Bearer ${token}` } });
      setPrefs((p) => ({ ...p, marketing: value }));
      toast.success(value ? 'Emails marketing activés' : 'Emails marketing désactivés');
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4">
      <Section title="Emails marketing et conseils" icon={Mail}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm text-muted-foreground leading-relaxed">
              Nouveautés produit, guides ICT, promotions, packs. Environ 1 à 2 emails par semaine.
            </div>
          </div>
          <Switch checked={prefs.marketing} onCheckedChange={toggle} disabled={saving} data-testid="email-marketing-switch" />
        </div>
      </Section>

      <Section title="Emails transactionnels" icon={Shield}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm text-muted-foreground leading-relaxed">
              Confirmations d&apos;abonnement, factures Stripe, limites d&apos;analyses, réinitialisation de mot de passe. Nécessaires au fonctionnement du service.
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase tracking-widest shrink-0">Toujours actifs</span>
        </div>
      </Section>
    </div>
  );
}

// ==================== DELETE ACCOUNT DIALOG ====================
function DeleteAccountDialog({ open, onClose }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (confirm !== 'SUPPRIMER') return toast.error('Tapez SUPPRIMER pour confirmer');
    if (!password) return toast.error('Saisissez votre mot de passe');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/auth/delete-account`, { password }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Compte supprimé. Merci pour votre confiance.');
      logout();
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-400 flex items-center gap-2">
            <Trash2 className="w-5 h-5" /> Supprimer définitivement mon compte
          </DialogTitle>
          <DialogDescription className="text-left space-y-2">
            <span className="block">Cette action est <strong>irréversible</strong> après 14 jours. Vous perdrez :</span>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Tous vos trades (réels et paper)</li>
              <li>Votre historique d&apos;analyses IA</li>
              <li>Vos préférences et watchlist</li>
              <li>Votre abonnement actif sera résilié</li>
            </ul>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Mot de passe (pour confirmer votre identité)</Label>
            <PasswordInput data-testid="delete-account-password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          <div className="space-y-2">
            <Label>Tapez <span className="font-mono text-red-400">SUPPRIMER</span> pour confirmer</Label>
            <Input data-testid="delete-account-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="SUPPRIMER" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button variant="destructive" onClick={submit} disabled={loading || confirm !== 'SUPPRIMER' || !password} data-testid="confirm-delete-account">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Supprimer définitivement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== SHARED ====================
function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-card/60 border border-white/10 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-semibold uppercase tracking-widest">{title}</h2>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/40 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-right">{children}</span>
    </div>
  );
}
